import languages from './searchLanguages.json';

const supported = new Set(languages.map(language => language.code));
const languageAliases = { kok: 'gom', kon: 'gom', ori: 'or', od: 'or' };
/** Map regional and alternate language codes to Bhashini's supported codes. */
export function normalizeSearchLanguage(value = '') {
  const code = String(value).toLowerCase().replace(/_/g, '-').split('-')[0];
  return languageAliases[code] || code;
}

// Uses the same configured Bhashini inference endpoint as the site's chatbot.
// https://dibd-bhashini.gitbook.io/bhashini-apis/available-models-for-usage
const translationService = process.env.REACT_APP_SEARCH_TRANSLATION_SERVICE_ID || 'bhashini/iiith/nmt-all';
const detectionService = 'bhashini/indic-lang-detection-all';
const cache = new Map();

/** Send a cancellable detection/translation request and validate its response. */
async function infer(source, taskType, config, signal) {
  const response = await fetch(process.env.REACT_APP_BHASHINI_ENDPOINT_URL, {
    method: 'POST', signal,
    headers: { Authorization: process.env.REACT_APP_BHASHINI_API_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipelineTasks: [{ taskType, config }], inputData: { input: [{ source }] } }),
  });
  if (!response.ok) throw new Error('Translation service unavailable');
  const data = await response.json();
  const output = data?.pipelineResponse?.find(task => task.taskType === taskType)?.output?.[0]
    || data?.pipelineResponse?.[0]?.output?.[0];
  if (!output) throw new Error('Invalid translation response');
  return output;
}

/** Translate queries with bounded caching, timeout, and direct-search fallback. */
export async function translateSearchQuery(query, selectedLanguage = 'en', { signal } = {}) {
  const original = query.trim();
  const preferred = normalizeSearchLanguage(selectedLanguage);
  const isAscii = Array.from(original).every(character => character.codePointAt(0) <= 0x7f);
  const key = `${preferred}:${original}`;
  if (!original) return { query: original, language: preferred, unavailable: false };
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (cache.has(key)) return cache.get(key);
  // English queries do not need an API call. Non-Latin input is detected even
  // when the page language is English; selected languages also support mixed input.
  if (preferred === 'en' && isAscii) {
    return { query: original, language: 'en', unavailable: false };
  }
  if (!process.env.REACT_APP_BHASHINI_ENDPOINT_URL || !process.env.REACT_APP_BHASHINI_API_KEY) {
    return { query: original, language: preferred, unavailable: true };
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 10000);
  try {
    let language = supported.has(preferred) ? preferred : 'en';
    try {
      const output = await infer(original, 'txt-lang-detection', { serviceId: detectionService }, controller.signal);
      const prediction = output.langPrediction?.[0];
      const detected = normalizeSearchLanguage(prediction?.langCode);
      if (supported.has(detected) && (prediction.langScore == null || prediction.langScore >= 0.6)) language = detected;
    } catch (error) {
      if (controller.signal.aborted) throw error;
      // A selected Indian language remains usable when language detection fails.
      if (language === 'en' && !isAscii) throw error;
    }
    let translated = original;
    if (language !== 'en') {
      const output = await infer(original, 'translation', {
        serviceId: translationService, language: { sourceLanguage: language, targetLanguage: 'en' },
      }, controller.signal);
      if (typeof output.target !== 'string' || !output.target.trim()) throw new Error('Empty translation');
      translated = output.target.trim();
    }
    const result = { query: translated, language, unavailable: false };
    if (cache.size >= 100) cache.delete(cache.keys().next().value);
    cache.set(key, result);
    return result;
  } catch (error) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return { query: original, language: preferred, unavailable: true };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

/** Normalize Unicode and whitespace without removing language-specific marks. */
export const normalizeSearch = (value = '') => String(value).normalize('NFKC').toLocaleLowerCase().replace(/[\u200b-\u200d\ufeff]/g, '').replace(/\s+/g, ' ').trim();

const normalizedDocuments = new WeakMap();
const stopWords = new Set(['a', 'an', 'the', 'of', 'for', 'to', 'in', 'and', 'is', 'on']);
const compareResults = (a, b) => b.score - a.score || a.title.localeCompare(b.title);

/** Normalize large PDF bodies once per document, instead of on every keystroke. */
function prepareDocument(document) {
  if (!normalizedDocuments.has(document)) {
    const title = normalizeSearch(document.title);
    const text = normalizeSearch(document.text);
    normalizedDocuments.set(document, {
      title, text,
      searchable: `${title} ${normalizeSearch(document.url)} ${normalizeSearch(document.keywords)} ${text}`,
    });
  }
  return normalizedDocuments.get(document);
}

// Common Hindi and Roman Hindi terms also find the English source pages.
const aliases = {
  'ऋण': 'loan', 'लोन': 'loan', 'योजना': 'scheme', 'योजनाएं': 'schemes',
  'शिक्षा': 'education', 'शैक्षिक': 'education', 'वार्षिक': 'annual',
  'रिपोर्ट': 'report', 'निविदा': 'tender', 'निविदाएं': 'tenders',
  'संपर्क': 'contact', 'अल्पसंख्यक': 'minority', 'रोजगार': 'career',
  'छात्रवृत्ति': 'fellowship', 'कारीगर': 'artisans', 'सूचना': 'information',
  'yojana': 'scheme', 'shiksha': 'education', 'rin': 'loan',
};

/** Require every query term and rank title matches ahead of body-text matches. */
export function searchDocuments(documents, query, { translated = false, sort = true } = {}) {
  const normalized = normalizeSearch(query);
  if (!normalized) return [];
  const words = translated ? normalized.replace(/[.,!?;:।]/g, ' ').split(/\s+/).filter(word => word && !stopWords.has(word)) : normalized.split(' ');
  const terms = (words.length ? words : [normalized]).map(term => [
    term, aliases[term],
    /^[a-z]{4,}s$/.test(term) && !term.endsWith('ss') ? term.slice(0, -1) : undefined,
  ].filter(Boolean));
  const results = documents.map(document => {
    const { title, text, searchable } = prepareDocument(document);
    if (!terms.every(variants => variants.some(term => searchable.includes(term)))) return null;
    const score = (title === normalized ? 100 : 0) + terms.reduce((sum, variants) => sum + (variants.some(term => title.includes(term)) ? 10 : 1), 0);
    const firstMatch = terms.flat().map(term => text.indexOf(term)).find(index => index >= 0);
    const position = firstMatch ?? 0;
    const start = Math.max(0, position - 65);
    const snippet = document.text ? `${start ? '…' : ''}${document.text.slice(start, start + 190)}${document.text.length > start + 190 ? '…' : ''}` : document.url || 'PDF document';
    return { ...document, score, snippet };
  }).filter(Boolean);
  return sort ? results.sort(compareResults) : results;
}

/** Merge both query languages, deduplicate URLs, and sort only the final results. */
export function searchMultilingualDocuments(documents, query, translatedQuery) {
  const merged = new Map();
  const matches = searchDocuments(documents, query, { sort: false });
  if (translatedQuery && normalizeSearch(translatedQuery) !== normalizeSearch(query)) {
    matches.push(...searchDocuments(documents, translatedQuery, { translated: true, sort: false }));
  }
  for (const match of matches) {
    if (!match.url) continue;
    if (!merged.has(match.url) || merged.get(match.url).score < match.score) merged.set(match.url, match);
  }
  return [...merged.values()].sort(compareResults);
}

let pendingIndex;
/** Lazily fetch the shared index once; allow retry after a failed request. */
export function loadSearchIndex() {
  if (!pendingIndex) {
    pendingIndex = Promise.all([
      fetch(`${process.env.PUBLIC_URL || ''}/search-index.json`).then(response => {
        if (!response.ok) throw new Error('Search index unavailable');
        return response.json();
      }),
      import('./pdfAssets.generated'),
    ]).then(([documents, assets]) => documents.map(document => ({
      ...document, url: document.assetId ? assets.default[document.assetId] : document.url,
    }))).catch(error => { pendingIndex = undefined; throw error; });
  }
  return pendingIndex;
}
